import React, { useCallback } from "react";

import Col from "antd/es/col";
import Radio from "antd/es/radio";
import Row from "antd/es/row";
import { FormattedMessage, useIntl } from "react-intl";

import Role from "@mapmycustomers/shared/enum/Role";

import Checkbox from "@app/component/input/Checkbox";
import RadioGroup from "@app/component/input/RadioGroup";
import { reportsWeeklyCardAnalytics } from "@app/scene/reports/reportsAnalytics";
import Iam from "@app/types/Iam";
import WeeklyReportSetting from "@app/types/report/WeeklyReportCardSettings";

import weeklyReportsOption from "../../utils/weeklyReportSettingOptions.json";
import Section from "../Section";

import styles from "./TeamLeaderboard.module.scss";

interface Props {
  formData: WeeklyReportSetting["value"];
  me?: Iam;
  onChange?: (formData: WeeklyReportSetting["value"]) => void;
}

const TeamLeaderBoard: React.FC<Props> = ({ formData, me, onChange }) => {
  const intl = useIntl();

  const handleToggle = useCallback(
    (enabled: boolean) => {
      onChange?.({ ...formData, teamLeaderboard: { ...formData.teamLeaderboard, enabled } });
      reportsWeeklyCardAnalytics.clicked([`Team Leaderboard Toggle ${enabled ? "On" : "Off"}`]);
    },
    [formData, onChange]
  );

  return (
    <Section
      active={formData.teamLeaderboard.enabled}
      description={intl.formatMessage({
        id: "reports.weeklyReportCard.teamLeaderboard.description",
        defaultMessage:
          "See a quick glance of how you and your teammates are performing based on a selected metric. As of now, we only support one (1) metric per email.",
        description: "Title for Team Leaderboard Section",
      })}
      disabled={!formData.enabled}
      onToggleActivation={handleToggle}
      title={intl.formatMessage({
        id: "reports.weeklyReportCard.teamLeaderboard",
        defaultMessage: "Team Leaderboard",
        description: "Title for Team Leaderboard Section",
      })}
      tooltip={
        formData.teamLeaderboard.enabled
          ? intl.formatMessage({
              id: "reports.weeklyReportCard.disableSection",
              defaultMessage: "Toggle to hide this section in your email",
              description: "Title for toggle on weekly reports",
            })
          : intl.formatMessage({
              id: "reports.weeklyReportCard.enableSection",
              defaultMessage: "Toggle to show this section in your email",
              description: "Title for toggle on weekly reports",
            })
      }
    >
      <RadioGroup
        className={styles.radioGroup}
        disabled={!formData.enabled || !formData.teamLeaderboard.enabled}
        onChange={(value) =>
          onChange?.({
            ...formData,
            teamLeaderboard: { ...formData.teamLeaderboard, config: [value] },
          })
        }
        value={
          Array.isArray(formData.teamLeaderboard.config) &&
          formData.teamLeaderboard.config.length > 0
            ? formData.teamLeaderboard.config[0]
            : undefined
        }
      >
        <Row>
          {weeklyReportsOption.map((item, key) => (
            <Col key={key} lg={8} sm={12}>
              <Radio value={item.value}>{item.label}</Radio>
            </Col>
          ))}
        </Row>
      </RadioGroup>
      {me?.role.key !== Role.MEMBER && (
        <div className={styles.leaderboardComparison}>
          <span className={styles.leaderboardComparisonTitle}>
            {intl.formatMessage({
              id: "reports.weeklyReportCard.teamLeaderboard.compareBy",
              defaultMessage: "Compare by team member or team?",
              description: "Leader Board Comparison Section",
            })}
          </span>
          <RadioGroup
            className={styles.leaderboardComparisonRadioGroup}
            disabled={!formData.enabled || !formData.teamLeaderboard.enabled}
            onChange={(leaderboardComparison) =>
              onChange?.({
                ...formData,
                teamLeaderboard: { ...formData.teamLeaderboard, leaderboardComparison },
              })
            }
            value={formData.teamLeaderboard.leaderboardComparison}
          >
            <Radio value="user">
              <FormattedMessage
                id="reports.weeklyReportCard.teamLeaderboard.compareBy.user"
                defaultMessage="Team members (default)"
                description="compare by team member option"
              />
            </Radio>
            <Radio value="team">
              <FormattedMessage
                id="reports.weeklyReportCard.teamLeaderboard.compareBy.team"
                defaultMessage="Team"
                description="Compare by team option"
              />
            </Radio>
          </RadioGroup>
        </div>
      )}
      <Checkbox
        checked={formData.teamLeaderboard.resultsCapped}
        className={styles.leaderboardComparisonRadioGroup}
        disabled={!formData.enabled || !formData.teamLeaderboard.enabled}
        onChange={(resultsCapped) =>
          onChange?.({
            ...formData,
            teamLeaderboard: {
              ...formData.teamLeaderboard,
              resultsCapped,
            },
          })
        }
      >
        <FormattedMessage
          id="reports.weeklyReportCard.teamLeaderboard.show5members"
          defaultMessage="Only show top 5 team members"
          description="Only show top 5 members option"
        />
      </Checkbox>
    </Section>
  );
};

export default TeamLeaderBoard;
